import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaBars, FaTimes, FaUser, FaGlobe, FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import PolicyModal from './PolicyModal';

const Navbar = ({ onLoginClick, onRegisterClick }) => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [policyModal, setPolicyModal] = useState({ isOpen: false, type: null });

  const languages = [
    { code: 'en', label: 'EN', fullName: 'English' },
    { code: 'ro', label: 'RO', fullName: 'Romanian' },
    { code: 'fr', label: 'FR', fullName: 'French' },
    { code: 'es', label: 'ES', fullName: 'Spanish' },
    { code: 'de', label: 'DE', fullName: 'German' },
    { code: 'pt', label: 'PT', fullName: 'Portuguese' },
  ];

  const navLinks = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.projects'), href: '#projects' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.reload();
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsLangDropdownOpen(false);
  };

  const smoothScrollTo = (id) => {
    const element = document.querySelector(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass-dark shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center cursor-pointer"
              onClick={() => smoothScrollTo('#home')}
            >
              <img
                src="/logo-new.png"
                alt="Sheba Smart Home"
                className="h-16 w-auto"
              />
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => smoothScrollTo(link.href)}
                  className="text-white hover:text-[#00d4ff] transition-colors duration-300 font-medium"
                >
                  {link.name}
                </button>
              ))}
            </div>

            {/* Desktop: Auth Buttons & Language Switcher */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Language Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                  className="glass px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-white/10 transition-colors"
                >
                  <FaGlobe className="text-[#00d4ff]" />
                  <span className="text-white font-medium">{currentLang.label}</span>
                  <FaChevronDown className="text-white/60 text-xs" />
                </button>

                {isLangDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 glass-strong rounded-xl shadow-lg overflow-hidden">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center justify-between ${
                          i18n.language === lang.code ? 'bg-[#00d4ff]/20' : ''
                        }`}
                      >
                        <span className="text-white">{lang.label}</span>
                        <span className="text-white/60 text-sm">{lang.fullName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {user ? (
                <>
                  <div className="text-white flex items-center space-x-2">
                    <FaUser className="text-[#00d4ff]" />
                    <span className="text-sm">{user.full_name}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="btn-secondary flex items-center space-x-2 !py-2 !px-4"
                  >
                    <span>{t('nav.logout')}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onLoginClick}
                    className="btn-secondary !py-2 !px-6"
                  >
                    {t('nav.login')}
                  </button>
                  <button
                    onClick={onRegisterClick}
                    className="btn-primary !py-2 !px-6"
                  >
                    {t('nav.register')}
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-white p-2"
            >
              {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-dark border-t border-white/10"
          >
            <div className="px-4 py-6 space-y-4">
              {/* Mobile Navigation Links */}
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => smoothScrollTo(link.href)}
                  className="block w-full text-left text-white hover:text-[#00d4ff] transition-colors py-2"
                >
                  {link.name}
                </button>
              ))}

              <div className="border-t border-white/10 pt-4 mt-4">
                {/* Mobile Language Selector */}
                <div className="mb-4">
                  <label className="text-white/60 text-sm mb-2 block">{t('nav.language')}</label>
                  <select
                    value={i18n.language}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="w-full glass px-4 py-3 rounded-lg text-white focus:outline-none focus:border-[#00d4ff]"
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code} className="bg-[#0a0e27]">
                        {lang.label} - {lang.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mobile Auth Buttons */}
                {user ? (
                  <>
                    <div className="text-white flex items-center space-x-2 mb-4 glass rounded-lg px-4 py-3">
                      <FaUser className="text-[#00d4ff]" />
                      <span>{user.full_name}</span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full btn-secondary !py-2"
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        onLoginClick();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full btn-secondary !py-2 mb-2"
                    >
                      {t('nav.login')}
                    </button>
                    <button
                      onClick={() => {
                        onRegisterClick();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full btn-primary !py-2"
                    >
                      {t('nav.register')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </motion.nav>

      {/* Policy Modal */}
      <PolicyModal
        isOpen={policyModal.isOpen}
        onClose={() => setPolicyModal({ isOpen: false, type: null })}
        type={policyModal.type}
      />
    </>
  );
};

export default Navbar;
